import React, { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import {
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Typography,
  Paper
} from "@mui/material";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "common/redux/core/root.reducer";
import EditDocumentTypeDialog from "./EditDocumentTypeDialog";
import { getToken } from "common/services/tempService";
import { buildGdxApiUrl } from "common/utils/api.util";

interface ManageDocumentTypesDialogProps {
  onClose: () => void;
}

const ManageDocumentTypesDialog: React.FC<ManageDocumentTypesDialogProps> = ({
  onClose
}) => {
  const [documentTypeOptions, setDocumentTypeOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [refresh, setRefresh] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<any>(null);

  // Fetch document types from API
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const res = await axios.get(
          buildGdxApiUrl("gdx/database/document-types"),
          {
            headers: {
              // Authorization: token
            }
          }
        );
        setDocumentTypeOptions(res.data || []);
      } catch (err) {
        setDocumentTypeOptions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDocumentTypes();
  }, [refresh]);

  const handleDelete = (doc: any) => {
    setDocToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete?.id) return;
    try {
      const token = await getToken();
      await axios.delete(
        buildGdxApiUrl(`gdx/database/document-type/${docToDelete.id}/hard`),
        {
          headers: {
            // Authorization: token
          }
        }
      );
      setDeleteDialogOpen(false);
      setDocToDelete(null);
      setRefresh((r) => !r);
    } catch (err) {
      // Optionally show error
      setDeleteDialogOpen(false);
      setDocToDelete(null);
    }
  };

  const handleEdit = (doc: any) => {
    setSelectedDoc(doc);
    setOpenEditModal(true);
  };

  const handleAddDoc = () => {
    setSelectedDoc(null);
    setOpenEditModal(true);
  };

  return (
    <>
      <>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              Manage Document Types
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddDoc}
              sx={{
                backgroundColor: "var(--branding, #FFD600)",
                color: "#222",
                fontWeight: 600,
                boxShadow: 2,
                "&:hover": { backgroundColor: "#FFEA70" }
              }}
            >
              Add Document Type
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      width: 50,
                      minWidth: 50,
                      maxWidth: 60
                    }}
                  >
                    No
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      width: 180,
                      minWidth: 150,
                      maxWidth: 220
                    }}
                  >
                    Document Type Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      width: 220,
                      minWidth: 180,
                      maxWidth: 300
                    }}
                  >
                    Document Type Description
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      width: 100,
                      minWidth: 80,
                      maxWidth: 120
                    }}
                    align="center"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(!documentTypeOptions || documentTypeOptions.length === 0) &&
                  !loading && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        sx={{ color: "gray", fontStyle: "italic" }}
                      >
                        No document types found
                      </TableCell>
                    </TableRow>
                  )}

                {loading && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{ color: "gray", fontStyle: "italic" }}
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                )}

                {!loading &&
                  documentTypeOptions?.map((doc: any, index: number) => (
                    <TableRow key={doc.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell
                        sx={{
                          width: 180,
                          minWidth: 150,
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {doc.name && doc.name.length > 15 ? (
                          <Tooltip title={doc.name} arrow>
                            <span>{doc.name.slice(0, 15) + "..."}</span>
                          </Tooltip>
                        ) : (
                          doc.name || (
                            <span style={{ color: "red" }}>Missing Name</span>
                          )
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: 220,
                          minWidth: 180,
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {doc.description?.length > 25 ? (
                          <Tooltip title={doc.description} arrow>
                            <span>{doc.description.slice(0, 25) + "..."}</span>
                          </Tooltip>
                        ) : (
                          doc.description || "-"
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: 220
                        }}
                      >
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(doc)}
                          sx={{ backgroundColor: "#FFFDE7", mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(doc)}
                          sx={{ backgroundColor: "#FFFDE7" }}
                          disabled={!doc.id}
                          title={!doc.id ? "Invalid document type" : "Delete"}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: "var(--branding, #FFD600)",
              color: "#222",
              fontWeight: 600,
              boxShadow: 2,
              "&:hover": { backgroundColor: "#FFEA70" }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </>
      <EditDocumentTypeDialog
        open={openEditModal}
        document={selectedDoc}
        onClose={() => {
          setOpenEditModal(false);
          setRefresh((r) => !r); // Refresh list after add/edit
        }}
      />

      {/* Delete Confirmation Modal */}
      <MuiDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <MuiDialogTitle sx={{ fontWeight: 600, mt: 1, color: "black" }}>
          Delete Document Type
        </MuiDialogTitle>
        <MuiDialogContent>
          <Typography sx={{ fontWeight: 400, mt: 1, color: "black" }}>
            Are you sure you want to delete{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {docToDelete?.name}
            </span>{" "}
            ?
          </Typography>
        </MuiDialogContent>
        <MuiDialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="contained"
            sx={{
              borderColor: "#b71c1c",
              backgroundColor: "#fff0f0",
              color: "#b71c1c",
              "&:hover": {
                borderColor: "#b71c1c",
                backgroundColor: "#fff0f0",
                color: "#b71c1c"
              }
            }}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </MuiDialogActions>
      </MuiDialog>
    </>
  );
};

export default ManageDocumentTypesDialog;
